import { CloudArrowUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/solid';
import React, { useRef, useState } from 'react';

import { useFileUpload } from './useFileUpload';

interface FileUploadProps {
  onFileSelect: (file: File) => Promise<boolean>;
  onFileDelete: () => void;
  acceptedFileTypes: string;
  maxFileSize: number; // in MB
  currentFile?: File | null;
}

const FileUpload: React.FC<FileUploadProps> = (props) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { file, handleFiles, removeFile } = useFileUpload(props);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files[0]);
    }
  };

  const onButtonClick = () => {
    inputRef.current.click();
  };

  return (
    <div className="flex flex-col items-center">
      <div
        className={`w-full cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
          dragActive ? 'border-rgpd-blue-dark bg-rgpd-blue-light/10' : 'border-gray-300'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={onButtonClick}
      >
        <input
          ref={inputRef}
          type="file"
          accept={props.acceptedFileTypes}
          onChange={handleChange}
          className="hidden"
        />
        {file ? (
          <div className="flex items-center justify-center space-x-2">
            <DocumentIcon className="h-6 w-6 text-rgpd-blue-dark" />
            <span className="text-sm font-medium text-gray-700">{file.name}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeFile();
              }}
              className="text-red-500 hover:text-red-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <CloudArrowUpIcon className="mx-auto h-10 w-10 text-gray-400" />
            <p className="text-sm text-gray-600">
              Glissez-déposez un fichier ici, ou cliquez pour sélectionner
            </p>
            <p className="text-xs text-gray-500">
              {props.acceptedFileTypes}
              {' '}
              (MAX.
              {props.maxFileSize}
              Mo)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
